import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { IActionButton } from '../../components/ActionButtons'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import { Billable, TimeAction } from '../../config/registration/timeConfig'
import ActionHeader from '../../components/Render/ActionHeader'
import TimeList, { ITimeListSettings, TimeListFilter, TimeListProps } from '../../components/List/Time/TimeList'
import useUser from '../../hooks/useUser'
import CsvReportButton from '../../components/Button/CsvReportButton'
import { timeBaseUrl } from '../Times'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import useModules from '../../hooks/module/useModules'
import { ModuleType } from '../../context/GlobalContext'
import useRoles, { Role, rolesTimes } from '../../hooks/useRoles'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Field } from '@fluentui/react-components'
import AttributesList, { parseAttributeToParams } from '../../components/Form/Attributes/AttributesList'
import useViewport, { Viewport } from '../../hooks/useViewport'
import Loading from '../../components/Loading'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import { IFileListSettings } from '../../components/List/File/FileList'
import DateRange from '../../components/DatePicker/DateRange'
import UserPicker from '../../components/TagPicker/UserPicker'
import FilePicker from '../../components/TagPicker/FilePicker'
import { useTranslation } from 'react-i18next'
import BillablePicker from '../../components/TagPicker/BillablePicker'
import { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { Stack } from '@fluentui/react'

export const parseFileSettingsToParams = (settings: ITimeListSettings): any => {
    return {
        ...settings.filter,
        attributes: parseAttributeToParams(settings.filter.attributes),
    }
}

export default function TimeOverview(props: TimeListProps) {
    const dispatch = useDispatch()
    const modules = useModules()
    const currentUser = useUser()
    const roles = useRoles()
    const { t: translate } = useTranslation()

    const storeSettings = useSelector<Store, ITimeListSettings>(state => state.times.overview.settings)
    const [settings, setSettings] = useState<ITimeListSettings>(undefined)

    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('<', Viewport.XL) ? 'vertical' : 'horizontal'), [viewport])

    const updateFilter = (filter: Partial<TimeListFilter>): any => {
        if (filter === undefined) {
            setSettings({
                ...settings,
                filter: {
                    ...settings.filter,
                },
            })
        }
        if (settings?.filter === undefined) {
            setSettings({
                ...settings,
                filter: {},
            })
        } else {
            setSettings({
                ...settings,
                filter: {
                    ...settings?.filter,
                    ...filter,
                    attributes: [...(settings.filter.attributes || []), ...(filter.attributes || [])].filter(
                        (value, index, array) => array.indexOf(value) === index,
                    ),
                },
            })
        }
    }

    // Set settings once store settings have been fetched
    useEffect(() => {
        if (settings === undefined && storeSettings.filter !== undefined) {
            setSettings(storeSettings)
        }
    }, [storeSettings])

    useEffect(() => {
        //Only needed for first load of this component per session, any subsequent loads on the same session will have
        //valid values for the user array. If another user logs in, the state will be reset and this gets run again.
        if (!storeSettings.filter?.users) {
            dispatch({
                type: TimeAction.OverviewSettings,
                payload: {
                    ...storeSettings,
                    filter: {
                        ...storeSettings?.filter,
                        users: roles.any(rolesTimes) ? [currentUser.id] : [],
                    },
                },
            })
        }
    }, [currentUser?.id])

    //Update store settings
    useEffect(() => {
        dispatch({
            type: TimeAction.OverviewSettings,
            payload: {
                ...settings,
            },
        })
    }, [settings])

    const actionButtonItems: IActionButton[] = useMemo(
        () => [
            {
                key: 'add-entity-registration',
                title: translate('action.create.time.title'),
                secondaryText: translate('action.create.time.subtitle'),
                iconName: 'Timer',
                route: `${props.baseUrl}/add`,
            },
            {
                key: 'time-suggestion-overview',
                title: translate('action.time.suggestion.title'),
                secondaryText: translate('action.time.suggestion.title'),
                iconName: 'TimeEntry',
                route: `${props.baseUrl}/suggestion`,
                filter: () => props.baseUrl === timeBaseUrl,
            },
        ],
        [props.baseUrl],
    )

    if (settings === undefined && settings?.filter === undefined) {
        return <Loading />
    }

    return (
        <>
            <ActionHeader actionButtonItems={actionButtonItems}>
                <DateRange
                    orientation={orientation}
                    startValue={settings.filter.from}
                    endValue={settings.filter.to}
                    changeStartDate={date => updateFilter({ from: date })}
                    changeEndDate={date => updateFilter({ to: date })}
                    showLabel
                />
                <Field label={translate('time.filter.user')} orientation={orientation}>
                    <UserPicker
                        selectedOptions={settings.filter.users || []}
                        onOptionsSelect={(_, selectedOptions) => updateFilter({ users: selectedOptions })}
                    />
                </Field>
                <Field label={translate('time.filter.billable')} orientation={orientation}>
                    <BillablePicker
                        selectedOptions={settings.filter.billable || []}
                        onOptionsSelect={(_, selectedOptions) =>
                            updateFilter({ billable: selectedOptions as Billable[] })
                        }
                    />
                </Field>
                {modules.has(ModuleType.Invoices) && (
                    <Field label={translate('time.filter.isBilled')} orientation={orientation}>
                        <BooleanCombobox
                            selectedOptions={[settings.filter.isBilled?.toString() || 'mixed']}
                            onOptionSelect={(_, data) =>
                                updateFilter({
                                    isBilled: data.checked === 'mixed' ? undefined : data.checked,
                                })
                            }
                        />
                    </Field>
                )}
                {props?.global && (
                    <Field label={translate('time.filter.file')} orientation={orientation}>
                        <FilePicker
                            selectedOptions={settings.filter.files || []}
                            onOptionsSelect={(_, selectedOptions) => updateFilter({ files: selectedOptions })}
                        />
                    </Field>
                )}
                {props?.global && modules.has(ModuleType.Invoices) && (
                    <Accordion collapsible>
                        <AccordionItem value="attributes">
                            <AccordionHeader>{translate('time.filter.attributes')}</AccordionHeader>
                            <AccordionPanel>
                                <AttributesList
                                    orientation={orientation}
                                    entityType={EntityAttributeType.File}
                                    context={[AttributeContextType.File]}
                                    viewType={AttributeViewType.Filter}
                                    entity={settings.filter}
                                    setFieldValue={() => updateFilter({})}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}
            </ActionHeader>
            {roles.all([Role.Report]) && (
                <Stack horizontal>
                    <CsvReportButton<TimeListFilter>
                        generationType={DocumentGenerationType.TimeXls}
                        payload={() => parseFileSettingsToParams(settings)}
                    />
                </Stack>
            )}
            <TimeList {...props} billing settings={settings} />
        </>
    )
}
